import { createContext, useState, useRef } from "react";
import { Action, ObjectType, defaultNoteTheme } from "../data/constants";
import { useUndoRedo, useTransform, useSelect } from "../hooks";
import { message } from "antd";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { getDrawDBApp } from "ee/selectors/drawdbSelectors";
import { editDrawDB } from "ee/actions/drawdbActions";
import { debounce } from "lodash";

export const NotesContext = createContext(null);

export default function NotesContextProvider({ children }) {
  const { t } = useTranslation();
  const [notes, setNotes] = useState([]);
  const { transform } = useTransform();
  const { setUndoStack, setRedoStack } = useUndoRedo();
  const { selectedElement, setSelectedElement } = useSelect();

  const dispatch = useDispatch();
  const drawDBApp = useSelector(getDrawDBApp);

  const debouncedDispatch = useRef(
    debounce((action) => {
      dispatch(action);
    }, 300),
  ).current;

  const addNote = (data, addToHistory = true) => {
    if (data) {
      setNotes((prev) => {
        const temp = prev.slice();
        temp.splice(data.id, 0, data);
        const notes = temp.map((t, i) => ({ ...t, id: i }));
        debouncedDispatch(
          editDrawDB({
            id: drawDBApp.id,
            layout: { ...drawDBApp.layout, notes },
          }),
        );
        return notes;
      });
    } else {
      const height = 88;
      setNotes((prev) => {
        const notes = [
          ...prev,
          {
            id: prev.length,
            x: transform.pan.x,
            y: transform.pan.y - height / 2,
            title: `note_${prev.length}`,
            content: "",
            color: defaultNoteTheme,
            height,
          },
        ];
        debouncedDispatch(
          editDrawDB({
            id: drawDBApp.id,
            layout: { ...drawDBApp.layout, notes },
          }),
        );
        return notes;
      });
    }
    if (addToHistory) {
      setUndoStack((prev) => [
        ...prev,
        {
          action: Action.ADD,
          element: ObjectType.NOTE,
          message: t("add_note"),
        },
      ]);
      setRedoStack([]);
    }
  };

  const deleteNote = (id, addToHistory = true) => {
    if (addToHistory) {
      message.success(t("note_deleted"));
      setUndoStack((prev) => [
        ...prev,
        {
          action: Action.DELETE,
          element: ObjectType.NOTE,
          data: notes[id],
          message: t("delete_note", { noteTitle: notes[id].title }),
        },
      ]);
      setRedoStack([]);
    }
    setNotes((prev) => {
      const notes = prev
        .filter((e) => e.id !== id)
        .map((e, i) => ({ ...e, id: i }));
      debouncedDispatch(
        editDrawDB({
          id: drawDBApp.id,
          layout: { ...drawDBApp.layout, notes },
        }),
      );
      return notes;
    });
    if (id === selectedElement.id) {
      setSelectedElement((prev) => ({
        ...prev,
        element: ObjectType.NONE,
        id: -1,
        open: false,
      }));
    }
  };

  const updateNote = (id, values) => {
    setNotes((prev) => {
      const notes = prev.map((t) => {
        if (t.id === id) {
          return {
            ...t,
            ...values,
          };
        }
        return t;
      });
      // debouncedDispatch(
      //   editDrawDB({
      //     id: drawDBApp.id,
      //     layout: { ...drawDBApp.layout, notes },
      //   }),
      // );
      return notes;
    });
  };

  return (
    <NotesContext.Provider
      value={{
        notes,
        setNotes,
        updateNote,
        addNote,
        deleteNote,
        notesCount: notes.length,
      }}
    >
      {children}
    </NotesContext.Provider>
  );
}
